import { safeGet, safeSet } from '@/lib/safeStorage';

/**
 * App-wide marketing attribution: the FIRST tagged visit on this device, sent once to the
 * backend with the first successful sign-in or signup.
 *
 *   - `captureFirstTouch(search)` runs once at boot on every route. The first URL carrying any
 *     utm_source / utm_medium / utm_content is stored and never overwritten. Untagged visits
 *     store nothing, so a direct visit before the WhatsApp link doesn't hide the channel.
 *   - `pendingAttribution()` is what `/auth/login` and `/auth/signup` attach: the stored touch,
 *     or null once it has been sent (or there is none).
 *   - `markAttributionSent()` runs after the first 2xx from either call. The stored values stay
 *     untouched; a separate flag stops them being sent again.
 *
 * Separate from the event page's per-slug, last-touch `wyso.event-utm.<slug>` — see
 * docs/wiki/attribution.md.
 */

export const UTM_KEYS = /** @type {const} */ (['source', 'medium', 'content']);
const TOUCH_KEY = 'wyso.utm';
const SENT_KEY = 'wyso.utm-sent';

/**
 * Read utm_source / utm_medium / utm_content from `search` (e.g. window.location.search).
 * Returns null when the URL carries none of them.
 * @param {string} search
 * @returns {import('./attribution').Attribution | null}
 */
export function parseUtm(search) {
  const params = new URLSearchParams(search);
  const utm = {};
  for (const key of UTM_KEYS) {
    const value = params.get(`utm_${key}`)?.trim();
    if (value) utm[key] = value;
  }
  return Object.keys(utm).length ? utm : null;
}

/**
 * The stored first touch, or null. Only a plain object with string fields is trusted: a
 * tampered or stale-format value would otherwise 422 a sign-in once the backend validates it.
 */
function readTouch() {
  let stored;
  try {
    stored = JSON.parse(safeGet(TOUCH_KEY) ?? 'null');
  } catch {
    return null;
  }
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return null;
  const utm = {};
  for (const key of UTM_KEYS) {
    if (typeof stored[key] === 'string' && stored[key].trim()) utm[key] = stored[key].trim();
  }
  return Object.keys(utm).length ? utm : null;
}

/**
 * Store this visit's UTM params if they're the first this device has seen. No-op otherwise.
 * @param {string} search
 */
export function captureFirstTouch(search) {
  if (readTouch()) return;
  const fromUrl = parseUtm(search);
  if (fromUrl) safeSet(TOUCH_KEY, JSON.stringify(fromUrl));
}

/**
 * The attribution to attach to `/auth/login` or `/auth/signup`, or null when there is nothing
 * to send (no tagged visit, or already sent).
 * @returns {import('./attribution').Attribution | null}
 */
export function pendingAttribution() {
  if (safeGet(SENT_KEY)) return null;
  return readTouch();
}

/** Record that the backend has the first touch; later calls omit it. */
export function markAttributionSent() {
  safeSet(SENT_KEY, '1');
}
